import { Injectable, inject } from '@angular/core';

import {
    HeaderService as CoreHeaderService,
    DslService,
    DynamicLayoutService,
    HtmlNode,
    MENU_COUNTERS_PROVIDER,
    MenuAction,
    MenuActionsService,
    MenuCountersService,
    OnFeatureInit,
    SlotName,
} from '@frontend/vanilla/core';
import { AccountMenuOnboardingService } from '@frontend/vanilla/shared/account-menu';
import { HeaderSearchService } from '@frontend/vanilla/shared/header';
import { combineLatest, firstValueFrom } from 'rxjs';

import { HEADER_COMPONENTS_MAP } from './header-component-map';
import { HeaderMessagesService } from './header-messages.service';
import { HeaderMessagesComponent } from './header-messages/header-messages.component';
import { HeaderConfig } from './header.client-config';
import { HeaderComponent } from './header.component';
import { HeaderService } from './header.service';

@Injectable()
export class HeaderBootstrapService implements OnFeatureInit {
    private dynamicLayoutService = inject(DynamicLayoutService);
    private headerService = inject(HeaderService);
    private headerMessagesService = inject(HeaderMessagesService);
    private dslService = inject(DslService);
    private config = inject(HeaderConfig);
    private htmlNode = inject(HtmlNode);
    private accountMenuOnboardingService = inject(AccountMenuOnboardingService);
    private menuActionsService = inject(MenuActionsService);
    private headerSearchService = inject(HeaderSearchService);
    private coreHeaderService = inject(CoreHeaderService);
    private menuCountersService = inject(MenuCountersService);
    private avatarProviders = inject(MENU_COUNTERS_PROVIDER);

    async onFeatureInit() {
        this.headerMessagesService.init();
        this.dynamicLayoutService.addComponent(SlotName.Messages, HeaderMessagesComponent, null);
        this.dynamicLayoutService.setComponent(SlotName.Header, HeaderComponent, null);
        await firstValueFrom(this.config.whenReady);
        this.coreHeaderService.set(this.headerService);

        this.menuCountersService.registerProviders(this.avatarProviders);
        this.headerService.initProductHighlighting();
        this.headerService.initMenuItems();

        combineLatest([
            this.dslService.evaluateExpression<boolean>(this.config.isEnabledCondition),
            this.dslService.evaluateExpression<boolean>(this.config.disabledItems.disabled),
        ]).subscribe(([isEnabled, disableItems]) => {
            if (isEnabled) {
                this.htmlNode.toggleVisibilityClass('header', true);

                this.config.disabledItems.sections.forEach((section) => this.htmlNode.setCssClass(`${section}-hidden`, disableItems));

                this.headerService.show(disableItems ? this.config.disabledItems.sections : []);
            } else {
                this.htmlNode.toggleVisibilityClass('header', false);
                this.headerService.hide();
            }
        });

        // register all components lazily
        for (const key of Object.keys(HEADER_COMPONENTS_MAP)) {
            this.headerService.registerLazyCmp(key, HEADER_COMPONENTS_MAP[key]!);
        }

        if (!this.headerService.currentHighlightedProductName()) {
            this.headerService.setHighlightedProduct();
        }

        if (!this.headerService.itemDisabled(SlotName.HeaderTopItems)) {
            this.dslService.evaluateContent(this.config.elements.topSlotItems).subscribe((items) => {
                items.forEach((item) => {
                    this.headerService.getLazyComponent(item.type).then((componentType) => {
                        if (componentType) {
                            this.dynamicLayoutService.addComponent(SlotName.HeaderTopItems, componentType, null);
                        }
                    });
                });
            });
        }

        this.accountMenuOnboardingService.init(this.config.onboardingEnabled);
        this.menuActionsService.register(MenuAction.SEARCH_ICON_CLICK, () => {
            this.headerSearchService.click();
        });
    }
}
